import { useMemo, useState } from 'react'
import { EmptyState } from '@/shared/components/EmptyState'
import { Tooltip } from '@/shared/components/Tooltip'
import type { ProviderArtefactVersion, ProviderVersionFinding } from '@/lib/apiTypes'
import { useProviderVersionComparison } from '@/lib/hooks'
import { SEVERITY_RANK } from '@/lib/format'
import { FindingCells } from '@/features/providers/components/FindingCells'
import { FindingFilters } from '@/features/providers/components/FindingFilters'
import { PatchMark, entryAsFinding, type PatchState } from '@/features/providers/components/PatchMark'
import { filterCounts, matchesFixable, matchesSeverity, type FixableFilter, type SeverityFilter } from '@/features/providers/lib/findingFilters'

/** The expanded subtable: this version's findings behind the severity / fixable filter row.
 *  When a terramantle patched build exists for this version, a "Patched" column says per CVE
 *  whether that build fixes it - scan-verified once the patched tree has a completed scan, else
 *  "planned" from the patch manifest - and CVEs only the patched side knows about (introduced by
 *  the patch, or needing a major bump) are folded in as extra rows. */
export function VersionFindingsSubtable({ orgId, upstream, namespace, type, v }: {
  orgId: string
  upstream: string
  namespace: string
  type: string
  v: ProviderArtefactVersion
}) {
  const [severity, setSeverity] = useState<SeverityFilter>('all')
  const [fixable, setFixable] = useState<FixableFilter>('all')
  // Only a build that actually FIXED something gets the per-CVE Patched column and the
  // comparison fetch. For a zero-fix build the scan on both sides is of the same upstream base
  // tag, so the diff is a tree against itself: every CVE would render as "still present
  // (scan-verified)", and any CVE-database drift between the two scan runs would claim a fix or
  // an introduction that no code change can account for. Null here also skips the fetch.
  const build = v.build && v.build.fixed_count > 0 ? v.build : null
  const cmp = useProviderVersionComparison(orgId, upstream, namespace, type, build ? v.version : null)
  const patched = build

  // Per-CVE patched state + the extra rows the comparison contributes.
  const { stateByCve, extraFindings } = useMemo(() => {
    const stateByCve = new Map<string, PatchState>()
    const extraFindings: ProviderVersionFinding[] = []
    if (!patched) return { stateByCve, extraFindings }
    const full = patched.full_version
    const known = new Set(v.findings.map((f) => f.cve_id).filter(Boolean) as string[])
    const data = cmp.data
    if (data && data.patched_scanned) {
      for (const e of data.fixed) stateByCve.set(e.cve_id, { mark: '✓', tone: 'green', tip: `Fixed in ${full}: ${e.pkg ?? 'dependency'} ${e.from ?? '?'} → ${e.to ?? '?'} (scan-verified).` })
      for (const e of data.remaining) stateByCve.set(e.cve_id, { mark: '✗', tone: 'red', tip: `Still present in ${full} (scan-verified).` })
      for (const e of data.introduced) {
        stateByCve.set(e.cve_id, { mark: '✗', tone: 'red', tip: `Introduced by the patch: present in ${full} but not upstream. Investigate.` })
        if (!known.has(e.cve_id)) extraFindings.push(entryAsFinding(e))
      }
    } else if (data) {
      for (const e of data.planned) stateByCve.set(e.cve_id, { mark: '✓', tone: 'amber', tip: `Planned: ${e.pkg ?? 'dependency'} ${e.from ?? '?'} → ${e.to ?? '?'}. ${full} has not been scanned yet, so this is what the patch attempted, not what a scan verified.` })
    } else {
      for (const m of patched.manifest) stateByCve.set(m.cve_id, { mark: '✓', tone: 'amber', tip: `Planned: ${m.pkg ?? 'dependency'} ${m.from ?? '?'} → ${m.to ?? '?'} (from the patch manifest).` })
    }
    for (const r of data?.report_only ?? patched.report_only) {
      stateByCve.set(r.cve_id, { mark: '✗', tone: 'amber', tip: `Not auto-fixable: needs a major bump of ${r.pkg} (fixed in ${r.fixed_in}).` })
      if (!known.has(r.cve_id)) extraFindings.push(entryAsFinding(r))
    }
    return { stateByCve, extraFindings }
  }, [patched, cmp.data, v.findings])

  const allFindings = useMemo(() => [...v.findings, ...extraFindings], [v.findings, extraFindings])
  const counts = useMemo(() => filterCounts(allFindings, severity, fixable), [allFindings, severity, fixable])

  const filtered = useMemo(() => allFindings
    .filter((f) => matchesSeverity(f, severity))
    .filter((f) => matchesFixable(f, fixable))
    .slice()
    // Worst-first so the headline vulnerabilities surface at the top of the subtable.
    .sort((a, b) =>
      (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0) ||
      (b.cvss_score ?? 0) - (a.cvss_score ?? 0),
    ),
  [allFindings, severity, fixable])

  function patchStateFor(f: ProviderVersionFinding): PatchState {
    if (cmp.loading) return { mark: '…', tone: 'muted', tip: 'Checking the patched build…' }
    if (f.cve_id && stateByCve.has(f.cve_id)) return stateByCve.get(f.cve_id)!
    if (cmp.error) return { mark: '—', tone: 'muted', tip: 'Patched comparison unavailable right now.' }
    return { mark: '—', tone: 'muted', tip: cmp.data?.patched_scanned ? 'Not a CVE the patched build changes.' : 'Not in the patch manifest.' }
  }

  return (
    <div>
      {patched && cmp.data && !cmp.data.patched_scanned && (
        <div style={{ fontSize: '0.75rem', color: 'var(--amber)', marginBottom: 10 }}>
          {patched.full_version} has not been scanned yet: ✓ marks in the Patched column are planned by the patch, not verified.
        </div>
      )}
      <FindingFilters
        severity={severity} setSeverity={setSeverity}
        fixable={fixable} setFixable={setFixable}
        counts={counts}
      />
      {filtered.length === 0 ? (
        <EmptyState icon="⬡" title="No findings match your filter" />
      ) : (
        <div className="table-wrap table-scroll" style={{ maxHeight: 300 }}>
          <table>
            <thead>
              <tr>
                <th>
                  <Tooltip text="Advisory id, linked to its registry entry (NVD or GitHub Advisories)." placement="top">
                    <span tabIndex={0} className="cursor-help">CVE</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Severity as reported by the scanner for this advisory." placement="top">
                    <span tabIndex={0} className="cursor-help">Severity</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="The affected dependency and the version installed in this build." placement="top">
                    <span tabIndex={0} className="cursor-help">Package</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="First package version that carries the fix, where one exists." placement="top">
                    <span tabIndex={0} className="cursor-help">Fixed in</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Whether a fixed package version exists to bump to." placement="top">
                    <span tabIndex={0} className="cursor-help">Fixable</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="CVSS base score for the advisory, where it has one." placement="top">
                    <span tabIndex={0} className="cursor-help">CVSS</span>
                  </Tooltip>
                </th>
                {patched && (
                  <th>
                    <Tooltip text={`Whether terramantle's patched build ${patched.full_version} fixes this CVE: ✓ fixed, ✗ still present or introduced, amber = planned by the patch but not yet scan-verified.`} placement="top">
                      <span tabIndex={0} className="cursor-help">Patched</span>
                    </Tooltip>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((f, i) => (
                <tr key={`${f.cve_id ?? 'unknown'}-${f.pkg_name ?? '?'}-${i}`}>
                  <FindingCells f={f} />
                  {patched && <td><PatchMark state={patchStateFor(f)} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
